import { Box, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { ACCENT_BLUE, DAY_TOGGLE, FONT_SIZE, INK, MOTION, NEUTRAL, SURFACE } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";

/** One day: a short label, an on/off state, and an optional corner marker. */
export type DayToggleCell = {
	/** Stable identity handed back to `onToggle` — a grid cell key, an ISO date, a weekday index. */
	key: string | number;
	/** The glyph inside the chip (a day initial). Kept to 1–3 characters by the chip's width. */
	label: string;
	selected: boolean;
	/** The chip's accessible name — the visible label is an initial, so this carries the real meaning. */
	ariaLabel: string;
	/**
	 * Corner status marker. `color` is passed in (a semantic tone from the caller's own scale) so the
	 * marker can never disagree with whatever surface it is annotating.
	 *
	 * `ringColor` overrides the hairline drawn around the dot. It defaults to the page surface, which
	 * separates the dot from a filled chip — right for a dark marker, but not enough on its own for a
	 * light one (a saturated yellow dot is ~1.6:1 on white however cleanly it is separated). A caller
	 * whose scale HAS a dark counterpart to its marker should pass it here, so a light marker keeps a
	 * legible edge on every chip state instead of dissolving into the pale ones.
	 */
	marker?: { color: string; ringColor?: string; tooltip?: string };
	/**
	 * Status wash for the cell while it is UNSELECTED — the caller's own scale, so a day that needs
	 * attention reads as that scale's colour rather than as a neutral chip with a dot on it.
	 *
	 * Unselected only, deliberately: selection is the chip's primary state and has to stay unambiguous
	 * down a whole week. A cell that is both washed and selected shows the selected fill, and its marker
	 * is what carries the status through.
	 */
	tone?: { bg: string; ink: string; border?: string };
	disabled?: boolean;
	dataTestId?: string;
};

/** A cluster of days rendered together — one per schedule week, with an optional caption. */
export type DayToggleGroup = {
	key: string | number;
	/** Small caption above the cluster (a week number). Omit for an uncaptioned strip. */
	label?: string;
	cells: DayToggleCell[];
};

type Props = {
	groups: DayToggleGroup[];
	onToggle: (cell: DayToggleCell) => void;
	/**
	 * Fill of a selected chip: `primary` follows the org's brand colour, `success` marks a
	 * confirmed/settled pattern, and `accent` is the fixed `ACCENT_BLUE` for a surface whose design
	 * specifies that blue rather than the configurable brand.
	 */
	tone?: "primary" | "success" | "accent";
	size?: "small" | "medium" | "large";
	/**
	 * Let each cluster fill its container instead of drawing fixed squares: the cells become equal
	 * fractions of one row and grow wide enough for a real word ("Sun", not "S").
	 *
	 * The strip form exists to sit in a dense table row, where a square is right and the width has to be
	 * predictable (see `dayToggleStripWidth`). A standalone form is the other case — one week per line,
	 * full day names, the width of the panel — and that is what this is for. Only the cell geometry
	 * changes; the state, the markers and the a11y are the same control.
	 */
	stretch?: boolean;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/** Width (px) of one week cluster, so a hoisted caption can sit exactly over its chips. */
const clusterWidth = (cellCount: number, cellSize: number): number => cellCount * cellSize + Math.max(0, cellCount - 1) * DAY_TOGGLE.CHIP_GAP;

/**
 * Total width (px) of the whole strip.
 *
 * Exported because a LIST of these only lines up if the strip is a fixed-width column: CSS grid sizes
 * `auto` columns per grid container, so sibling rows that are each their own grid resolve different
 * column widths and the strips end up staggered. Pinning the column to this value is what makes one
 * hoisted caption header correct for every row (and avoids depending on `subgrid`).
 */
export const dayToggleStripWidth = (groups: DayToggleGroup[], size: "small" | "medium" = "small"): number => {
	const cellSize = size === "small" ? DAY_TOGGLE.CELL_SM : DAY_TOGGLE.CELL_MD;
	const clusters = groups.reduce((total, group) => total + clusterWidth(group.cells.length, cellSize), 0);
	return clusters + Math.max(0, groups.length - 1) * DAY_TOGGLE.CLUSTER_GAP;
};

const GROUP_LABEL_SX = {
	fontSize: FONT_SIZE.LABEL_MICRO,
	fontWeight: 700,
	letterSpacing: "0.08em",
	textTransform: "uppercase" as const,
	color: INK.DISABLED,
	lineHeight: 1.2,
};

/**
 * A grid of on/off day chips, grouped into clusters — the "which days does this pattern cover?" control.
 *
 * Each chip is a real `<button aria-pressed>` so the whole strip is keyboard- and screen-reader-operable,
 * and each may carry a corner marker for a day that still needs attention. Generic over what a day means:
 * the caller owns the cell keys, the labels, the grouping (one cluster per schedule week is the common
 * case), and the marker's colour. For a single either/or use `SystemFlatSwitch`; for selectable value
 * chips use `SystemChips`.
 */
export const DayToggleGrid: FC<Props> = ({ groups, onToggle, tone = "primary", size = "small", stretch = false, dataTestId, sx }) => {
	const cellSize = size === "small" ? DAY_TOGGLE.CELL_SM : size === "medium" ? DAY_TOGGLE.CELL_MD : DAY_TOGGLE.CELL_LG;
	// One place resolves the tone, so the chip fill, its hover, its focus ring and the `accent` opt-out
	// of the brand palette can't drift apart.
	const toneColors = (theme: Theme) =>
		tone === "accent"
			? { main: ACCENT_BLUE.MAIN, dark: ACCENT_BLUE.DARK, contrastText: ACCENT_BLUE.ON_ACCENT }
			: { main: theme.palette[tone].main, dark: theme.palette[tone].dark, contrastText: theme.palette[tone].contrastText };

	return (
		<Box
			data-testid={dataTestId}
			sx={[
				stretch
					? { display: "flex", flexDirection: "column", gap: `${DAY_TOGGLE.CLUSTER_GAP}px` }
					: { display: "flex", alignItems: "flex-end", flexWrap: "wrap", gap: `${DAY_TOGGLE.CLUSTER_GAP}px` },
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{groups.map((group) => (
				<Box key={group.key} sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
					{group.label && (
						<Box component="span" sx={[GROUP_LABEL_SX, { pl: "1px" }]}>
							{group.label}
						</Box>
					)}
					<Box
						sx={
							stretch
								? { display: "grid", gridTemplateColumns: `repeat(${group.cells.length}, minmax(0, 1fr))`, gap: `${DAY_TOGGLE.CHIP_GAP}px` }
								: { display: "flex", gap: `${DAY_TOGGLE.CHIP_GAP}px` }
						}
					>
						{group.cells.map((cell) => {
							const chip = (
								<Box
									component="button"
									type="button"
									key={cell.key}
									disabled={cell.disabled}
									aria-pressed={cell.selected}
									aria-label={cell.ariaLabel}
									data-testid={cell.dataTestId}
									onClick={() => onToggle(cell)}
									sx={(theme) => {
										const colors = toneColors(theme);
										return {
											position: "relative",
											// `auto` under `stretch`: the grid column owns the width, so a cell can be wide
											// enough for a word. Height stays on the size scale either way.
											width: stretch ? "auto" : cellSize,
											height: cellSize,
											flexShrink: 0,
											p: 0,
											// Rounded square rather than a circle: a 7-up row of squares reads as one week
											// block, where circles read as seven separate beads.
											borderRadius: 1,
											// A cell's own `tone` only applies while it is unselected — see `DayToggleCell.tone`.
											border: `1px solid ${cell.selected ? colors.main : (cell.tone?.border ?? NEUTRAL[200])}`,
											backgroundColor: cell.selected ? colors.main : (cell.tone?.bg ?? NEUTRAL[50]),
											color: cell.selected ? colors.contrastText : (cell.tone?.ink ?? INK.DISABLED),
											font: "inherit",
											fontSize: FONT_SIZE.LABEL_XS,
											fontWeight: 700,
											lineHeight: 1,
											cursor: cell.disabled ? "default" : "pointer",
											opacity: cell.disabled ? 0.5 : 1,
											transition: theme.transitions.create(["background-color", "border-color", "color", "transform"], {
												duration: MOTION.DURATION.CONTROL,
												easing: MOTION.EASE_OUT,
											}),
											"@media (hover: hover) and (pointer: fine)": {
												"&:hover:not(:disabled)": {
													borderColor: colors.main,
													backgroundColor: cell.selected ? colors.dark : alpha(colors.main, 0.1),
													// A toned cell keeps its own ink through the hover wash, which is the accent's
													// tint — the status colour is the point of the cell, not a resting decoration.
													color: cell.selected ? colors.contrastText : (cell.tone?.ink ?? INK.DISABLED),
												},
											},
											"&:active:not(:disabled)": { transform: "scale(0.94)" },
											"&:focus-visible": { outline: `2px solid ${colors.main}`, outlineOffset: 1 },
											"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active:not(:disabled)": { transform: "none" } },
										};
									}}
								>
									{cell.label}
									{cell.marker && (
										<Box
											component="span"
											aria-hidden
											sx={{
												position: "absolute",
												top: DAY_TOGGLE.MARKER_OFFSET,
												right: DAY_TOGGLE.MARKER_OFFSET,
												width: DAY_TOGGLE.MARKER,
												height: DAY_TOGGLE.MARKER,
												borderRadius: "50%",
												backgroundColor: cell.marker.color,
												// DEFAULT: no `ringColor` → the page surface, the original behaviour.
												border: `${DAY_TOGGLE.MARKER_RING}px solid ${cell.marker.ringColor ?? SURFACE}`,
												"@keyframes dayToggleMarkerPop": {
													from: { opacity: 0, transform: "scale(0.6)" },
													to: { opacity: 1, transform: "scale(1)" },
												},
												animation: `dayToggleMarkerPop ${MOTION.DURATION.POP}ms ${MOTION.EASE_OUT}`,
												"@media (prefers-reduced-motion: reduce)": { animation: "none" },
											}}
										/>
									)}
								</Box>
							);
							return cell.marker?.tooltip ? (
								<InHouseTooltip key={cell.key} title={cell.marker.tooltip}>
									{chip}
								</InHouseTooltip>
							) : (
								chip
							);
						})}
					</Box>
				</Box>
			))}
		</Box>
	);
};

export default DayToggleGrid;
